import {
  S3Client,
  PutObjectCommand,
  GetObjectCommand,
  ListObjectsV2Command,
  DeleteObjectsCommand,
} from "@aws-sdk/client-s3";
import { S3_CONFIG } from "./s3Config";

const cliente = new S3Client({
  region: S3_CONFIG.region,
  credentials: S3_CONFIG.credentials,
  // Evita que el SDK calcule checksums automáticos (CRC32) sobre el Body:
  // con Blob/File en el navegador esto rompe con "e.getReader is not a
  // function" en versiones recientes de @aws-sdk/client-s3.
  requestChecksumCalculation: "WHEN_REQUIRED",
  responseChecksumValidation: "WHEN_REQUIRED",
});

const PREFIJO_PROYECTOS = "proyectos/";

const REGEX_DIACRITICOS = new RegExp(
  "[" + String.fromCharCode(0x0300) + "-" + String.fromCharCode(0x036f) + "]",
  "g"
);

const quitarDiacriticos = (texto) => texto.normalize("NFD").replace(REGEX_DIACRITICOS, "");

const slugificar = (texto) =>
  quitarDiacriticos(texto)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);

const sufijoAleatorio = () => {
  if (window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID().slice(0, 8);
  }
  return Math.random().toString(36).slice(2, 10);
};

// Genera un identificador de carpeta único para un proyecto nuevo, a
// partir de su nombre (legible) + un sufijo aleatorio (garantiza que dos
// proyectos con el mismo nombre no choquen en S3).
export const generarSlugProyecto = (nombre) => {
  const base = slugificar(nombre) || "proyecto";
  return `${base}-${sufijoAleatorio()}`;
};

const slugificarTitulo = (titulo) => slugificar(titulo) || "paso";

// Construye la ruta (key) de S3 para un paso/versión/tipo de archivo
// determinado. tipo es "cargado" o "generado".
export const rutaArchivoPaso = ({
  proyectoId,
  pasoNumero,
  tituloPaso,
  version,
  tipo,
  nombreArchivo,
}) =>
  `${PREFIJO_PROYECTOS}${proyectoId}/paso-${pasoNumero}-${slugificarTitulo(
    tituloPaso
  )}/v${version}/${tipo}/${nombreArchivo}`;

const rutaMetadata = (proyectoId) => `${PREFIJO_PROYECTOS}${proyectoId}/metadata.json`;

// Guarda (crea o sobreescribe) el metadata.json de un proyecto.
export const guardarMetadataProyecto = async (proyecto) => {
  await cliente.send(
    new PutObjectCommand({
      Bucket: S3_CONFIG.bucket,
      Key: rutaMetadata(proyecto.id),
      Body: JSON.stringify(proyecto),
      ContentType: "application/json",
    })
  );
};

// Lista todos los proyectos guardados en el bucket, leyendo el
// metadata.json de cada carpeta proyectos/<id>/. Si algún proyecto tiene
// un metadata.json corrupto o ilegible, se omite (no rompe la lista
// completa) y se reporta en consola.
export const listarProyectos = async () => {
  const respuesta = await cliente.send(
    new ListObjectsV2Command({
      Bucket: S3_CONFIG.bucket,
      Prefix: PREFIJO_PROYECTOS,
      Delimiter: "/",
    })
  );

  const prefijos = (respuesta.CommonPrefixes || []).map((p) => p.Prefix);

  const proyectos = await Promise.all(
    prefijos.map(async (prefijo) => {
      const proyectoId = prefijo.slice(PREFIJO_PROYECTOS.length, -1);
      try {
        const objeto = await cliente.send(
          new GetObjectCommand({ Bucket: S3_CONFIG.bucket, Key: rutaMetadata(proyectoId) })
        );
        const texto = await objeto.Body.transformToString();
        return JSON.parse(texto);
      } catch (error) {
        console.error(`No se pudo leer metadata.json de "${proyectoId}".`, error);
        return null;
      }
    })
  );

  return proyectos
    .filter(Boolean)
    .sort((a, b) => new Date(b.fechaCreacion) - new Date(a.fechaCreacion));
};

// Sube un archivo (File/Blob) al paso y versión indicados.
export const subirArchivoPaso = async ({
  proyectoId,
  pasoNumero,
  tituloPaso,
  version,
  tipo,
  archivo,
  nombreArchivo,
}) => {
  const key = rutaArchivoPaso({
    proyectoId,
    pasoNumero,
    tituloPaso,
    version,
    tipo,
    nombreArchivo: nombreArchivo || archivo.name,
  });
  await cliente.send(
    new PutObjectCommand({
      Bucket: S3_CONFIG.bucket,
      Key: key,
      Body: archivo,
      ContentType: archivo.type || "application/octet-stream",
    })
  );
  return key;
};

// Elimina todos los objetos de un proyecto (todas las versiones y pasos).
// Con el versionado del bucket activado, esto deja "delete markers" en
// vez de destruir las versiones anteriores — recuperable desde la
// consola de S3 si algo se borra por error.
export const eliminarProyectoS3 = async (proyectoId) => {
  const prefijo = `${PREFIJO_PROYECTOS}${proyectoId}/`;
  let continuationToken;
  const claves = [];

  do {
    const respuesta = await cliente.send(
      new ListObjectsV2Command({
        Bucket: S3_CONFIG.bucket,
        Prefix: prefijo,
        ContinuationToken: continuationToken,
      })
    );
    (respuesta.Contents || []).forEach((objeto) => claves.push({ Key: objeto.Key }));
    continuationToken = respuesta.IsTruncated ? respuesta.NextContinuationToken : undefined;
  } while (continuationToken);

  if (claves.length === 0) return;

  // DeleteObjects admite máximo 1000 claves por llamada.
  for (let i = 0; i < claves.length; i += 1000) {
    const lote = claves.slice(i, i + 1000);
    await cliente.send(
      new DeleteObjectsCommand({
        Bucket: S3_CONFIG.bucket,
        Delete: { Objects: lote },
      })
    );
  }
};
